import { ColDef } from 'ag-grid-enterprise'
import { CustomReportVariable } from '../../../../../../redux/context/customReports/typesVariable'
import { getCellBackgroundColor } from './renderingUtils'
import { calculateCategoryNameWidth } from './columnDefinitions'
import CustomCategoryRowCellRenderer from '../CustomCategoryRowCellRenderer'
import { TransposedRowData } from './transposedRowData'

interface TransposedColumnDefsOptions {
  variables: CustomReportVariable[]
  rows: TransposedRowData[]
  companyLabel?: string
  dimensionLabel?: string
  leafLabel?: string
}

export const buildTransposedColumnDefs = ({
  variables,
  rows,
  companyLabel = 'Company',
  dimensionLabel = 'Dimension',
  leafLabel = ''
}: TransposedColumnDefsOptions): ColDef<TransposedRowData, any>[] => {
  const commonHeaderStyle = {
    autoHeaderHeight: true,
    wrapHeaderText: true
  }

  const allCompanies = rows.map(row => row.company?.name).filter(Boolean)
  const shouldShowCompanyColumn = new Set(allCompanies).size > 1

  const allDimensions = rows.map(row => row.dimension?.name ?? null)
  const shouldShowDimensionColumn = new Set(allDimensions).size > 1

  const categoryNameWidth = calculateCategoryNameWidth(rows.map(r => r.categoryName))

  const cols: ColDef<TransposedRowData, any>[] = [
    ...(shouldShowCompanyColumn
      ? [
          {
            field: 'company.name' as const,
            headerName: companyLabel,
            width: 150,
            pinned: 'left' as const,
            suppressMovable: true,
            ...commonHeaderStyle,
            cellStyle: (params: any) => ({
              backgroundColor: getCellBackgroundColor(params.data?.leafCategory)
            })
          } as ColDef<TransposedRowData, any>
        ]
      : []),
    ...(shouldShowDimensionColumn
      ? [
          {
            field: 'dimension.name' as const,
            headerName: dimensionLabel,
            width: 150,
            pinned: 'left' as const,
            suppressMovable: true,
            ...commonHeaderStyle,
            cellStyle: (params: any) => ({
              backgroundColor: getCellBackgroundColor(params.data?.leafCategory)
            })
          } as ColDef<TransposedRowData, any>
        ]
      : []),
    {
      field: 'categoryName',
      headerName: leafLabel,
      suppressMovable: true,
      pinned: 'left',
      width: categoryNameWidth,
      ...commonHeaderStyle,
      cellStyle: (params: any) => ({
        backgroundColor: getCellBackgroundColor(params.data?.leafCategory)
      })
    },
    ...variables.map(
      variable =>
        ({
          field: `var_${variable.id}`,
          headerName: variable.name || variable.translation || `Variable ${variable.id}`,
          ...commonHeaderStyle,
          headerComponentParams: {
            template: `<div class="ag-cell-label-container-category" role="presentation">
              <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div data-ref="eLabel" class="ag-header-cell-label-category" role="presentation">
                <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
              </div>
            </div>`
          },
          width: 120,
          type: 'numericColumn',
          suppressMovable: true,
          editable: false,
          cellRenderer: CustomCategoryRowCellRenderer,
          cellStyle: (params: any) => ({
            fontWeight: variable.style?.fontWeight ? 'bold' : 'normal',
            fontStyle: variable.style?.fontStyle || 'normal',
            backgroundColor: getCellBackgroundColor(params.data?.leafCategory, variable, params?.value)
          }),
          cellClassRules: {
            bold: () => !!variable.style?.fontWeight,
            italic: () => variable.style?.fontStyle === 'italic'
          },
          context: {
            category: variable,
            variable,
            isTruncated: false
          },
          valueGetter: (params: any) => {
            const categoryId = params.data?.categoryId
            if (!categoryId || !variable.balances) return undefined
            const balance = variable.balances.find((b: any) => b.groupId === categoryId)
            return balance?.value
          }
        } as ColDef<TransposedRowData, any>)
    )
  ]

  return cols
}
